import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from "@angular/router";
import { DATE_PICKER_CONFIG } from "@app/components/material/matDatePicker/mat-date-picker.component";
import { OneButtonDialogComponent } from '@app/components/sharedDialogs/oneButtonDialog.component';
import { FuturePayment } from '@app/models/FuturePayment';
import { PaymentLineItem } from '@app/models/PaymentLineItem';
import { PaymentMethod } from '@app/models/PaymentMethod';
import { PaymentSchedule } from '@app/models/PaymentSchedule';
import { Property } from '@app/models/Property';
import { FeeChangeWarningDialog } from "@app/pages/payment/scheduleEdit/feeChangeWarningDialog";
import { PaymentApi } from '@app/services/apiServices/PaymentApi';
import { PaymentMethodApi } from '@app/services/apiServices/PaymentMethodApi';
import { PaymentScheduleApi } from '@app/services/apiServices/PaymentScheduleApi';
import { PropertyApi } from '@app/services/apiServices/PropertyApi';
import { AppDataService } from "@app/services/appData.service";
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { PaymentDateService, PaymentDateValidation } from '@app/services/PaymentDate.service';
import { PaymentTimingType } from '@app/services/shared/PaymentTimingType.enum';
import { PaymentUiState } from '@app/services/shared/PaymentUiState.enum';
import { SpinnerService } from '@app/services/spinner.service';
import { PaymentEditStore } from '@app/services/stores/PaymentEditStore';
import { UserStore } from '@app/services/stores/UserStore';
import { AppDate } from '@app/util/app-date';
import { moneyAdd, moneyNumber } from "@app/util/money-number";
import { filter, Observable, of, Subject } from "rxjs";
import { concatMap, finalize, take, takeUntil, tap } from "rxjs/operators";
import { ScheduleOneTimeViewDeleteDialog } from '../scheduleView/schedule-one-time-view-dialog-delete';
import { ScheduleViewCancelDialog } from '../scheduleView/scheduleViewCancel.dialog';

const StatusType: any = {
    ACCOUNT_BALANCE: 'account-balance',
    FIXED_AMOUNT: 'fixed-amount'
};

@Component({
    selector: 'app-one-time-edit',
    template: require('./one-time-edit.component.html'),
})
export class OneTimeEditComponent implements OnInit, OnDestroy {

    paymentScheduleId: number;
    paymentSchedule: Readonly<PaymentSchedule>;
    paymentMethods: PaymentMethod[];
    paymentMethodError: boolean;
    paymentMethodErrorMsg: string;

    property: Property;

    originalConvenienceFee: number;
    originalPaymentMethod: PaymentMethod;

    currentConvenienceFee: number;
    currentPaymentMethod: PaymentMethod;
    currentNickname: string;

    scheduleNicknameError: boolean;
    ACHMessage: string;
    cardMessage: string;

    amountToPay: number;
    originalAmountToPay: number;
    amountToPayErrorMsg: string;
    calculatingConvenienceFee: boolean = true;
    totalAmount: number;
    tcCheckbox: boolean = false;

    allowPayByCheck: boolean = true;
    allowPayByCard: boolean = true;

    maxPrincipalAmount: number;
    daysOfMonth: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31];
    timer: number;

    private destroy$ = new Subject<void>();

    /** config for date-picker, must be set in constructor! */
    paymentDatePickerConfig: DATE_PICKER_CONFIG = null;

    paymentDate: AppDate = null;
    paymentDateError: string = null;

    uiManipulation: PaymentUiState;
    doneLoading: boolean = false;

    constructor(
        @Inject(MatDialog) private dialog: any,
        @Inject(SpinnerService) private spinnerService: SpinnerService,
        @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
        @Inject(PaymentEditStore) private paymentEditStore: PaymentEditStore,
        @Inject(PaymentApi) private paymentApi: PaymentApi,
        @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
        @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
        @Inject(PaymentMethodApi) private paymentMethodApi: PaymentMethodApi,
        @Inject(PropertyApi) private propertyApi: PropertyApi,
        @Inject(UserStore) private userStore: UserStore,
        @Inject(AppDataService) private appDataService: AppDataService,
        @Inject(Router) private router: Router,
        @Inject(ActivatedRoute) private route: ActivatedRoute,
        @Inject(PaymentDateService) private paymentDateService: PaymentDateService,) {
    }

    ngOnInit() {

        this.ACHMessage = "Please note: you are making a payment through First Citizens Bank’s proprietary ACH payment system.";

        if (this.route.snapshot.params.scheduleId) {
            this.paymentScheduleId = this.route.snapshot.params.scheduleId;
        } else if (!window.__karma__) {
            console.error("No paymentScheduleId");
        }

        this.spinnerService.showSpinner();

        this.refreshData()

        this.maxPrincipalAmount = this.appDataService.appSettings.paymentLimits.maxPrincipalAmount;
        this.getPaymentSchedule().pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    setAllowCheckAndCard(allowPayByCheck: boolean, allowPayByCard: boolean) {
        this.allowPayByCheck = allowPayByCheck;
        this.allowPayByCard = allowPayByCard;
    }

    filterAllowedPaymentMethods(paymentMethods: PaymentMethod[]): PaymentMethod[] {
        return paymentMethods.filter(paymentMethod => {
            if (paymentMethod.type === 'card')
                return this.allowPayByCard
            else if (paymentMethod.type === 'ach')
                return this.allowPayByCheck
            else
                return true;
        });
    }

    getPaymentSchedule(): Observable<Property> {
        return this.paymentScheduleApi.paymentSchedulesIdGet(this.paymentScheduleId).pipe(
            tap(resp => {
                this.paymentSchedule = resp;
                this.setPaymentDate();
            }),
            concatMap(() => this.propertyApi.propertiesIdGet(this.paymentSchedule.propertyId)),
            tap(resp => {
                this.property = resp
            })
        );
    }

    paymentDateHelper() {
        this.dialog.open(OneButtonDialogComponent, {
            data: {
                "header": "Payment Date",
                "message": "These are the available payment dates based on your Management Company's payment cycle. Payments are processed Monday through Friday, excluding holidays.",
                "buttonText": "Close"
            }
        })
    }

    /** Use this instead of setting directly */
    async setPaymentDate() {

        let schDate = this.paymentSchedule?.startDate;
        this.paymentDate = AppDate.fromDate(new Date(schDate.year+'/'+schDate.month+'/'+schDate.day));

        const forSchedule = this.uiManipulation === PaymentUiState.SCHEDULE_PAYMENT;
        const cutoffHour = forSchedule ?
            this.appDataService.appConfig.CUTOFF_HOUR_SCHEDULE :
            this.appDataService.appConfig.CUTOFF_HOUR
        const cutoffMinute = forSchedule ?
            this.appDataService.appConfig.CUTOFF_MINUTE_SCHEDULE :
            this.appDataService.appConfig.CUTOFF_MINUTE

        const minDate = await this.paymentDateService.getNextValidPmtDate(AppDate.now().addDays(1), forSchedule)
        // ensure date pickers are set up
        this.paymentDatePickerConfig = {
            defaultDate: this.paymentDate,
            isDateEnabled: date => this.paymentDateService.validatePaymentDate(date, cutoffHour, cutoffMinute) === PaymentDateValidation.OK,
            minDate: minDate,
            validateDate: date => {
                return { value: date }
            },
            preloadYear: year => this.paymentDateService.updateHolidays([year]),
        }
    }

    paymentDateChange(value: unknown) {
        if(value instanceof AppDate) {
            this.paymentDate = value;
            if (this.paymentDatePickerConfig) {
               this.paymentDatePickerConfig.defaultDate = this.paymentDate
            }
            this.paymentDateError = null
        } else if ((typeof value) === 'string' ) {
            this.paymentDateError = value as string;
        }
    }

    refreshData() {

        try {
            this.paymentScheduleApi.paymentSchedulesIdGet(this.paymentScheduleId)
                .pipe(take(1)).subscribe(scheduleResp => {
                    this.paymentSchedule = Object.freeze(scheduleResp);
                    this.originalConvenienceFee = this.paymentSchedule.feeAmount;
                    this.amountToPay = this.paymentSchedule.principalAmount;
                    this.originalAmountToPay = this.amountToPay;
                    this.currentNickname = this.paymentSchedule.nickname;
                    const propertyId = this.paymentSchedule.propertyId;
                    this.propertyApi.propertiesIdGet(propertyId)
                        .pipe(take(1)).subscribe({ next: propertyResp => {
                            this.property = propertyResp;
                            this.propertyApi.propertiesValidateGet(false, this.property.hoa.pmc.couponId, this.property.hoa.couponId, this.property.couponId)
                                .pipe(take(1)).subscribe({ next: (validateResp) => {
                                    this.setAllowCheckAndCard(validateResp.achPayments, validateResp.cardPayments);
                                    const userId = this.userStore.getHoaHomeownerId();
                                    this.paymentMethodApi.homeownersIdPaymentMethodsGet(userId)
                                        .pipe(take(1)).subscribe({ next: (pmtMethodResp) => {
                                            this.paymentMethods = this.filterAllowedPaymentMethods(pmtMethodResp);
                                            const schedulePmtMethodId = Number(this.paymentSchedule.paymentMethodId);
                                            this.paymentMethods
                                                .filter(method => Number(method.id) === schedulePmtMethodId)
                                                .slice(0, 1)
                                                .forEach(method => {
                                                    this.currentPaymentMethod = method;
                                                    this.originalPaymentMethod = method;
                                                    this.getConvenienceFee(this.originalPaymentMethod).then(fee => {
                                                        this.currentConvenienceFee = fee;
                                                        this.totalAmount = +this.amountToPay + this.currentConvenienceFee;
                                                        this.calculatingConvenienceFee = false;
                                                        this.spinnerService.closeSpinner();
                                                        this.doneLoading = true;
                                                    })
                                                });
                                        },
                                        error:reason => {
                                            this.networkErrorService.handleNetworkError(reason, true)
                                                .pipe(take(1)).subscribe(shouldRetry => {
                                                    if (shouldRetry === true) {
                                                        this.refreshData()
                                                    }
                                                })
                                        } }
                                        );
                                },
                                error: reason => {
                                    this.networkErrorService.handleNetworkError(reason, true)
                                        .pipe(take(1)).subscribe(shouldRetry => {
                                            if (shouldRetry === true) {
                                                this.refreshData()
                                            }
                                        })
                                } }
                                )
                        },
                        error: reason => {
                            this.networkErrorService.handleNetworkError(reason, true)
                                .pipe(take(1)).subscribe(shouldRetry => {
                                    if (shouldRetry === true) {
                                        this.refreshData()
                                    }
                                })
                        } }
                        );

                });
        }
        catch(reason) {
            this.networkErrorService.handleNetworkError(reason, true)
                .pipe(take(1)).subscribe(shouldRetry => {
                    if (shouldRetry === true) {
                        this.refreshData()
                    }
                })
        }
    }

    /** convenience fee message should be based on if the convenience fee changes */
    getConvenienceFee(pmtMethod: PaymentMethod): Promise<number> {
        const amount = this.paymentSchedule.principalAmount ? this.paymentSchedule.principalAmount : 0;
        const pmtMethodState = pmtMethod.state || this.property.address.state;

        return new Promise<number>((resolve, reject) => {
            this.paymentApi.convenienceFeePropertyIdAmountGet(
                false,
                this.property.address.state,
                pmtMethodState,
                amount,
                this.property.hoa.pmc.couponId,
                this.property.hoa.couponId,
                pmtMethod.routingNumber != null ? "ACH" : "CARD",
                PaymentTimingType.SCHEDULED,
                this.paymentSchedule.frequency
            ).pipe(take(1)).subscribe({ next: resp => {
                resolve(resp.convenienceFee);
            }, error: err => reject(err) })
        });
    }

    scheduleNicknameChange() {
        this.scheduleNicknameError = !this.currentNickname || this.currentNickname.trim().length === 0;
    }

    scheduleNicknameBlur() {
        clearTimeout(this.timer);
        // @ts-ignore
        this.timer = setTimeout(() => {
            this.scheduleNicknameChange();
        },1000)
    }

    onChangePmtMethod() {
        this.calculatingConvenienceFee = true;
        this.getConvenienceFee(this.currentPaymentMethod).then(newFee => {

            this.currentConvenienceFee = newFee;

            if (this.originalConvenienceFee !== newFee) {
                const dialogRef = this.dialog.open(FeeChangeWarningDialog, {
                    disableClose: true,
                    data: {
                        // creates ngDialogData = data on the $scope
                        oldPaymentMethod: `${this.originalPaymentMethod.nickname} (${this.originalPaymentMethod.maskedId})`,
                        newPaymentMethod: `${this.currentPaymentMethod.nickname} (${this.currentPaymentMethod.maskedId})`,
                        oldFee: this.originalConvenienceFee,
                        newFee: newFee // ex: ngDialogData.newFee
                    },
                })
                dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
                    if (value === 'OK') {
                        this.updateAmount()
                    } else if (value === 'Cancel') {
                        this.currentPaymentMethod = this.originalPaymentMethod;
                        this.currentConvenienceFee = this.originalConvenienceFee;
                    } else {
                        // if it was closed for another reason, revert
                        this.currentPaymentMethod = this.originalPaymentMethod;
                        this.currentConvenienceFee = this.originalConvenienceFee;
                        console.error('Unexpected value returned from dialog', value)
                    }

                    this.calculatingConvenienceFee = false
                }, () => {})
            } else {
                this.updateAmount();
                this.calculatingConvenienceFee = false
            }
        })

    }

    save() {
        let updatedSchedule: PaymentSchedule;
        if ((this.currentConvenienceFee !== this.originalConvenienceFee) || (+this.amountToPay !== this.originalAmountToPay)) {
            // update convenience fees
            updatedSchedule = {
                ...this.paymentSchedule, // using spread we don't have to worry about mutations
                acceptedAmountsDate: AppDate.now().format("yyyy-MM-dd"),
                futurePayments: updateScheduleFeeItems(this.paymentSchedule, this.currentConvenienceFee, +this.amountToPay),
                paymentMethod: this.currentPaymentMethod,
                paymentMethodId: this.currentPaymentMethod.id,
                nickname: this.currentNickname,
                remainingPayments: null,
                endDate: null,
                feeAmount: this.currentConvenienceFee,
                paymentMethodType: this.currentPaymentMethod.type,
                principalAmount: +this.amountToPay,
                totalAmount: moneyNumber(this.totalAmount)
            }
        } else {
            updatedSchedule = {
                ...this.paymentSchedule,
                paymentMethod: this.currentPaymentMethod,
                paymentMethodId: this.currentPaymentMethod.id,
                nickname: this.currentNickname,
                remainingPayments: null,
                endDate: null,
                paymentMethodType: this.currentPaymentMethod.type
            }
        }

        const dObjPaymentDate = this.paymentDateService.dateObjectFromAppDate(this.paymentDate);

        updatedSchedule = {
            ...updatedSchedule,
            startDate: dObjPaymentDate,
            futurePayments: updatedSchedule.futurePayments?.map(item =>
                'date' in item ? { ...item, date: dObjPaymentDate } : item)
        }
        this.paymentSchedulesUpdate(updatedSchedule);
    }

    private paymentSchedulesUpdate (updateSchedule: PaymentSchedule) {
        const confirm$: Observable<void> = of(undefined);
        this.spinnerService.showSpinner();
        this.paymentScheduleApi.paymentSchedulesUpdatePost(updateSchedule)
        .subscribe({
            next: () => {
                this.spinnerService.closeSpinner();
                this.router.navigateByUrl('/properties', { state: { scheduleId: this.paymentScheduleId } } )
            },
            error: (err) => {
                this.spinnerService.closeSpinner();
                const errMsg = err?.error?.message || undefined;
                this.errorHandlerService.handleNetworkError(err, true, errMsg)
                    .pipe(take(1)).subscribe();
            }
        })
    }

    isDueAmountInvalid(due) {
        return (!due || due <= 0 || this.amountToPayErrorMsg || isNaN(due));
    }

    amountToPayError(amtToPay) {
        if (this.maxPrincipalAmount && amtToPay > this.maxPrincipalAmount) {
            this.amountToPayErrorMsg = 'Payment amount cannot exceed $' + this.maxPrincipalAmount;
        } else {
            this.amountToPayErrorMsg = undefined;
        }
    }

    submitDisabled() {

        let zeroAmountDetected = (this.amountToPay === 0 || this.amountToPay === null || this.amountToPay === undefined);

        return (this.currentPaymentMethod == null
            || (this.paymentSchedule == null || this.currentNickname == null || this.currentNickname.length === 0)
            || zeroAmountDetected
            || this.amountToPayErrorMsg
            || this.calculatingConvenienceFee
            || (this.paymentMethods && this.paymentMethods.length === 0)
            || !(this.currentPaymentMethod)
            || !this.tcCheckbox
            || this.scheduleNicknameError
            || this.paymentDateError != null
        );
    }

    updateAmount() {
        this.amountToPay = moneyNumber(this.amountToPay);
        this.amountToPayError(+this.amountToPay);
        this.paymentEditStore.setAmountToPay(this.amountToPay || null);
        this.updateConvenienceFee();
    }

    updateConvenienceFee() {
        let paymentMethodType: "ACH" | "CARD";
        if (this.currentPaymentMethod) {
            if (this.currentPaymentMethod.routingNumber !== null && this.currentPaymentMethod.routingNumber !== undefined)
                paymentMethodType = "ACH";
            else
                paymentMethodType = "CARD";
        }

        if (this.property && this.currentPaymentMethod && +this.amountToPay > 0) {
            this.calculatingConvenienceFee = true;
            // ACH Payment methods do not currently set their paymentMethod.state
            const billingState = this.currentPaymentMethod.state || this.property.address.state;

            this.paymentApi.convenienceFeePropertyIdAmountGet(false, this.property.address.state, billingState,
                this.amountToPay, this.property.hoa.pmc.couponId, this.property.hoa.couponId, paymentMethodType, PaymentTimingType.SCHEDULED, this.paymentSchedule.frequency)
                .pipe(take(1)).subscribe({ next: (data) => {
                    this.calculatingConvenienceFee = false;
                    this.currentConvenienceFee = data.convenienceFee;
                    this.totalAmount = +this.amountToPay + this.currentConvenienceFee;
                }, error: () => {
                    this.calculatingConvenienceFee = false;
                } });
        } else {
            this.currentConvenienceFee = 0;
            this.totalAmount = 0;
        }
    }

    clearDue() {
        this.amountToPay = 0.00;
        this.updateAmount();
    }

    clearAmountFieldWhen0(due) {
        if (due <= 0) {
            due = null;
            this.updateAmount();
        }
    }

    removeSchedule() {
        let dialogRef = this.dialog.open(ScheduleOneTimeViewDeleteDialog, {
            disableClose: true,
            data: {
                // accessed via ngDialogData.nickname in the template
                nickname: this.paymentSchedule && this.paymentSchedule.nickname
            }
        });

        dialogRef.afterClosed().pipe(
            take(1),
            takeUntil(this.destroy$),
            filter(value => value === 'Delete'),
            tap(() => this.spinnerService.showSpinner()),
            concatMap(() => this.paymentScheduleApi.paymentSchedulesIdDelete(this.paymentSchedule.id)),
            finalize(() => this.spinnerService.closeSpinner()),
            concatMap(() => {
                return this.dialog.open(OneButtonDialogComponent, {
                    disableClose: true,
                    data: {
                        header: 'Payment Canceled',
                        message:  'Your payment has been canceled',
                        buttonText: 'OK'
                    }
                }).afterClosed().pipe(take(1));
            })
        ).subscribe({
            next: () => {
                this.router.navigateByUrl('/properties');
            },
            error: (err) => {
                const errMsg = err?.error?.message || undefined;
                this.errorHandlerService.handleNetworkError(err, true, errMsg)
                    .pipe(take(1)).subscribe();
            }
        })
    }

    cancel() {
        this.router.navigateByUrl('/properties', { state: { scheduleId: this.paymentScheduleId } });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

/**
 * This is broken out into a function to make sure it's pure,
amount: newAmount,
 * which makes it easier to reason about.
*/
export function updateScheduleFeeItems(sched: PaymentSchedule, newFee: number, newAmount: number): FuturePayment[] {
return sched.futurePayments.map(futurePmt => {
    /** add up item amounts for updating total */
    let newTotal = 0;
    let updatedItems: PaymentLineItem[] = futurePmt.items.map(item => {
        // update each item
        if (/fee/i.test(item.description)) {
        newTotal = moneyAdd(newTotal, newFee);
        return {
                    ...item,
                    amount: newFee
                }
            } else {
                newTotal = moneyAdd(newTotal, newAmount)
                return {
                    ...item,
                    amount: newAmount
                }
            }
        });
        return {
            ...futurePmt,
            total: moneyNumber(newTotal),
            items: updatedItems,
        }
    })
}
